import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field } from '@/components/ui';
import { isApiError } from '@/lib/api';
import { sessionErrorKey } from './errors';
import styles from './StaffSessions.module.css';

export function RevokeSessionDialog({
  target,
  all,
  requiresReason,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  target: string;
  all?: boolean;
  requiresReason: boolean;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const invalid =
    requiresReason && (trimmed.length < 5 || trimmed.length > 200);
  const apiError = isApiError(error) ? error : null;

  return (
    <Dialog
      open
      variant="modal"
      title={
        all
          ? t('staffSessions.signOutEverywhereTitle')
          : t('staffSessions.signOutDeviceTitle')
      }
      onClose={onClose}
      dismissible={!pending}
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            disabled={invalid}
            loading={pending}
            onClick={() => onConfirm(trimmed)}
          >
            {all
              ? t('staffSessions.signOutEverywhere')
              : t('staffSessions.confirmSignOut')}
          </Button>
        </>
      }
    >
      <p className={styles.intro}>
        {all
          ? t('staffSessions.signOutEverywhereBody', { name: target })
          : t('staffSessions.signOutDeviceBody', { target })}
      </p>
      {requiresReason && (
        <Field
          label={t('staffSessions.reason')}
          hint={t('staffSessions.reasonHint')}
          error={
            reason.length > 0 && invalid
              ? t('staffSessions.reasonInvalid')
              : undefined
          }
        >
          <textarea
            className={styles.reasonInput}
            value={reason}
            maxLength={200}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      )}
      {Boolean(error) && (
        <p className={styles.error} role="alert">
          {apiError
            ? t(sessionErrorKey(apiError))
            : t('staffSessions.revokeFailed')}
          {apiError?.requestId
            ? ` ${t('common.requestId', { id: apiError.requestId })}`
            : ''}
        </p>
      )}
    </Dialog>
  );
}
